import { IBaseCompany, User } from './company.types';
import { ERiskLevel } from './compliance.types';
import { IRawMaterial } from './component.types';
import { EGeoDataStatus, IGeoDataIssue } from './geodata.types';
import { TBasicLogAction } from './log.types';
import { IBasePartnerCompany } from './partner.types';
import { GraphQlConnection } from './types';

export enum EDatasetColumn {
  FARM_ID = 'FARM_ID',
  ORIGIN_CLUSTER_ID = 'ORIGIN_CLUSTER_ID',
  FARM_NAME = 'FARM_NAME',
  FARM_OWNER_FIRST_NAME = 'FARM_OWNER_FIRST_NAME',
  FARM_OWNER_LAST_NAME = 'FARM_OWNER_LAST_NAME',
  FARM_OWNER_FULL_NAME = 'FARM_OWNER_FULL_NAME',
  NUMBER_OF_FARMERS = 'NUMBER_OF_FARMERS',
  COORDINATES_LAT_LNG = 'COORDINATES_LAT_LNG',
  COORDINATES_LNG_LAT = 'COORDINATES_LNG_LAT',
  LATITUDE = 'LATITUDE',
  LONGITUDE = 'LONGITUDE',
  FARM_SIZE_KM2 = 'FARM_SIZE_KM2',
  FARM_SIZE_HA = 'FARM_SIZE_HA',
  ADDITIONAL_CROPS = 'ADDITIONAL_CROPS',
  LAND_OWNERSHIP = 'LAND_OWNERSHIP',
  SITE_DESCRIPTION = 'SITE_DESCRIPTION',
}

export type CoordinateDatasetColumnType =
  | `${EDatasetColumn.COORDINATES_LAT_LNG}`
  | `${EDatasetColumn.COORDINATES_LNG_LAT}`;

export type FarmSizeDatasetColumnType =
  | `${EDatasetColumn.FARM_SIZE_KM2}`
  | `${EDatasetColumn.FARM_SIZE_HA}`;

export enum EDatasetErrorEntity {
  SITE = 'SITE',
  DOCUMENT = 'DOCUMENT',
  DATASET = 'DATASET',
}

export type TDatasetColumnType = `${EDatasetColumn}`;

export type TDatasetErrorEntity = `${EDatasetErrorEntity}`;

export interface DatasetError {
  entityId: string;
  entityType: TDatasetErrorEntity;
  errorMessage: string;
}

export interface IDatasetLogs {
  id: string;
  comment: string;
  userEmail: string;
  user: User;
  company: IBaseCompany;
  timestamp: Date;
  action: TBasicLogAction;
}

export interface IDataset {
  id: string;
  title: string;
  createdTimestamp: Date;
  modifiedTimestamp: Date;
  originCountry: string;
  documents: { id: string; title: string }[];

  /**
   * PartnerCompany base values
   */
  ownedBy: IBasePartnerCompany;
  rawMaterial: IRawMaterial;
  logs?: IDatasetLogs[];
  issues?: IGeoDataIssue[];
  riskStatus?: ERiskLevel;
  status: EGeoDataStatus;
  originalGeoDataId: string;

  /**
   * Should only be used in their respective state MAPPED / MAPPING_FAILED and VALIDATED / VALIDATION_FAILED
   */
  tableRepresentation?: DatasetTableRepresentation[];
  mappedColumns?: ColumnsData[];
}

export enum DatasetView {
  LIST = 'LIST',
  MAP = 'MAP',
}

export interface IDatasetsResponse {
  datasets: GraphQlConnection<IDataset>;
}

export interface ColumnsData {
  columnName: string;
  columnDataExample: string[]; // Array of strings representing first 5 items as examples
  mappedColumnName?: TDatasetColumnType; // The backend tries to automatically map columns based on the column name
}

export interface DatasetError {
  entityId: string;
  entityType: TDatasetErrorEntity;
  errorMessage: string;
}

export type EUDRDatasetCell = {
  value?: string | number | Date;
  error?: DatasetError[];
  isValid?: boolean;
};

export type EUDRDatasetRow = {
  farmId: EUDRDatasetCell;
  farmName?: EUDRDatasetCell;
  coordinates: EUDRDatasetCell;
  farmSizeHa?: EUDRDatasetCell;
  originClusterId?: EUDRDatasetCell;
  numberOfFarmers?: EUDRDatasetCell;
  additionalCrops?: EUDRDatasetCell;
  landOwnership?: EUDRDatasetCell;
  farmOwnerFullName?: EUDRDatasetCell;
  farmOwnerFirstName?: EUDRDatasetCell;
  farmOwnerLastName?: EUDRDatasetCell;
  siteDescription?: EUDRDatasetCell;
};

export type DatasetTableRepresentation = {
  columnsNames: TDatasetColumnType[];
  rows: EUDRDatasetRow[];
};

export interface DatasetProcessingError {
  datasetId: string;
  datasetErrors: DatasetError[];
  hasCellErrors: boolean;
}

export interface FileConfiguration {
  csvSeparator?: TCsvSeparator;
  decimalSeparator?: TDecimalSeparator;
}
export enum ECsvSeparator {
  'COMMA' = 'COMMA',
  'SEMICOLON' = 'SEMICOLON',
  'TAB' = 'TAB',
  'AUTO_DETECT' = 'AUTO_DETECT',
}

export enum EDecimalSeparator {
  'DOT' = 'DOT',
  'COMMA' = 'COMMA',
}

export type TCsvSeparator = `${ECsvSeparator}`;

export type TDecimalSeparator = `${EDecimalSeparator}`;
